// TES-516 only normalizes legacy defect image metadata in this existing defect reader.
// fallow-ignore-file complexity
// TES-516 touches the existing defect query shape that already overlaps with inspection query data.
// fallow-ignore-file code-duplication
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { useAuth } from "@/hooks/useAuth";
import { DefectSeverity, DefectStatus } from "@/types";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/lib/featureFlags";
import { cacheData, getCachedData, dequeueAll, type SyncQueueEntry } from "@/lib/offlineStore";
import { normalizeDefectImagePaths } from "@/lib/defectImages";
import {
  isUserQueryEnabled,
  normalizeTeamScopedQueryOptions,
  type TeamScopedQueryOptions,
} from "@/hooks/teamQueryOptions";

export interface DefectWithDetails {
  id: string;
  title: string;
  description: string | null;
  severity: DefectSeverity;
  status: DefectStatus;
  /** SAP-Integration (TES-545): persistierte Q-Meldungs-Nummer + Push-Sync-Status (Mig. 296). */
  sap_qmeldung_nr: string | null;
  sap_sync_status: "pending" | "success" | "error";
  created_at: string;
  updated_at: string;
  created_by: string | null;
  assigned_to: string | null;
  verifying_qm: string | null;
  images: string[] | null;
  due_date: string | null;
  inspection_id: string | null;
  checklist_item_id: string | null;
  resolution_notes: string | null;
  resolution_time_minutes: number | null;
  resolved_at: string | null;
  /**
   * TES-2325: who actually set the defect to `resolved` (server-stamped, Mig 404; exposed through
   * v_defects_with_details by Mig 417). Optional so offline-pending drafts (never resolved) stay valid.
   * Feeds the client four-eyes guard (isDefectSelfExecutor) — hides the verify button from the resolver.
   */
  resolved_by?: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  team_id: string | null;
  team_name: string | null;
  /** MGM-18 (TES-481): NULL = Top-Level-Mangel; gesetzt = Unteraufgabe (self-ref). */
  parent_defect_id?: string | null;
  inspection?: {
    id: string;
    status: "pending" | "in_progress" | "completed" | "cancelled" | "overdue";
    checklists: {
      name: string;
      version: string | null;
    };
  } | null;
  checklist_item?: {
    title: string;
  } | null;
  created_by_profile?: {
    first_name: string | null;
    last_name: string | null;
    display_name?: string | null;
    email: string;
    avatar_url?: string | null;
  } | null;
  assigned_to_profile?: {
    first_name: string | null;
    last_name: string | null;
    display_name?: string | null;
    email: string;
    avatar_url?: string | null;
  } | null;
  verifying_qm_profile?: {
    first_name: string | null;
    last_name: string | null;
    display_name?: string | null;
    email: string;
    avatar_url?: string | null;
  } | null;
  comments_count: number;
  isAssigneeUnavailable?: boolean;
  /** True when this defect was created offline and is still pending sync */
  isOfflinePending?: boolean;
}

/** Row shape of the `v_defects_with_details` view (generated). */
type DefectDetailsRow = Database["public"]["Views"]["v_defects_with_details"]["Row"];

/** Availability-relevant fields embedded in the view's `assigned_to_profile` JSON. */
interface AssigneeAvailability {
  is_disabled?: boolean | null;
  azure_disabled_at?: string | null;
}

/** Narrow the `Json`-typed embedded profile to its availability fields. */
function isAssigneeUnavailable(profile: DefectDetailsRow["assigned_to_profile"]): boolean {
  if (!profile || typeof profile !== "object") return false;
  const { is_disabled, azure_disabled_at } = profile as AssigneeAvailability;
  return Boolean(is_disabled) || (azure_disabled_at ?? null) !== null;
}

const DEFECTS_CACHE_KEY = "defects-list";

/**
 * Explicit column projection for `v_defects_with_details` (TES-1645).
 *
 * Replaces the previous `select("*")` overfetch: this query runs for every user and is
 * realtime-invalidated, so it must only pull the columns actually consumed by
 * {@link DefectWithDetails} (the `...defect` spread in {@link fetchDefects}).
 *
 * Intentionally OMITTED view columns (not consumed anywhere downstream):
 *   - `deleted_at`           — used only as a server-side `.is()` filter (PostgREST filters
 *                              independently of the projection), never read on the row.
 *   - `due_basis`            — not part of `DefectWithDetails`, no defects-list consumer.
 *   - `last_overdue_reminder_sent_at` — backend reminder bookkeeping, not surfaced in the list.
 *
 * Keep this list in sync with the `DefectWithDetails` interface above.
 */
const DEFECTS_VIEW_COLUMNS = [
  "id",
  "title",
  "description",
  "severity",
  "status",
  "sap_qmeldung_nr",
  "sap_sync_status",
  "created_at",
  "updated_at",
  "created_by",
  "assigned_to",
  "verifying_qm",
  "images",
  "due_date",
  "inspection_id",
  "checklist_item_id",
  "resolution_notes",
  "resolution_time_minutes",
  "resolved_at",
  // TES-2325: der reale `resolved`-Actor (Mig 404 an der Tabelle, Mig 417 an der View, server-gestempelt). Ohne ihn lief der
  // Client-Vier-Augen-Guard isDefectSelfExecutor tot (las immer undefined) — der Verify-Button
  // blieb dem Behebenden sichtbar. Server-RPC bleibt autoritativ; dies ist Defense-in-Depth.
  "resolved_by",
  "cancelled_at",
  "cancellation_reason",
  "team_id",
  "team_name",
  "parent_defect_id",
  "comments_count",
  "inspection",
  "checklist_item",
  "created_by_profile",
  "assigned_to_profile",
  "verifying_qm_profile",
].join(", ");

// Query key factory for consistent cache management
export const defectsKeys = {
  all: ["defects"] as const,
  list: (params: {
    userId?: string;
    canSeeAll: boolean;
    teamId?: string | null;
    externalDefectUser?: boolean;
  }) => [...defectsKeys.all, "list", params] as const,
  // TES-2042: Edit-Target-Key auch für die Seiten — der Fehler-Fallback räumt den geerrorten
  // Cache-Eintrag ab, sonst bliebe der Mangel bis zum GC im View-Fallback "gesperrt".
  editTarget: (defectId: string | null) => [...defectsKeys.all, "edit-target", defectId] as const,
};

/**
 * Map an offline sync-queue entry to a DefectWithDetails stub
 * so that offline-created defects appear in the list immediately.
 */
function offlineEntryToDefect(entry: SyncQueueEntry): DefectWithDetails {
  return offlinePayloadToDefect(entry.payload, entry.timestamp);
}

/**
 * Convert a raw offline payload into a DefectWithDetails stub.
 * Used for optimistic cache updates after offline queueing.
 */
export function offlinePayloadToDefect(
  p: Record<string, unknown>,
  timestamp?: string,
): DefectWithDetails {
  const now = timestamp || new Date().toISOString();
  return {
    id: (p.id as string) || `offline-${Date.now()}`,
    title: (p.title as string) || "",
    description: (p.description as string | null) || null,
    severity: ((p.severity as string) || "medium") as DefectSeverity,
    status: ((p.status as string) || "open") as DefectStatus,
    sap_qmeldung_nr: null,
    sap_sync_status: "pending",
    created_at: now,
    updated_at: now,
    created_by: (p.created_by as string) || null,
    assigned_to: (p.assigned_to as string) || null,
    verifying_qm: null,
    images: normalizeDefectImagePaths(p.images),
    due_date: (p.due_date as string) || null,
    inspection_id: (p.inspection_id as string) || null,
    checklist_item_id: (p.checklist_item_id as string) || null,
    resolution_notes: null,
    resolution_time_minutes: null,
    resolved_at: null,
    cancelled_at: null,
    cancellation_reason: null,
    team_id: (p.team_id as string) || null,
    team_name: null,
    inspection: null,
    checklist_item: null,
    created_by_profile: null,
    assigned_to_profile: null,
    verifying_qm_profile: null,
    comments_count: 0,
    isOfflinePending: true,
  };
}

export async function fetchDefects(
  userId: string | undefined,
  canSeeAll: boolean,
  isOnline: boolean,
  offlineEnabled: boolean,
  teamId?: string | null,
  /**
   * TES-626: external defect identities (partner_supplier / external_auditor /
   * customer_acceptance) are anchor-scoped server-side by RLS (mig 308). They are
   * neither creator nor assignee of the anchored defects, so the creator/assignee
   * involvement filter below would wrongly hide everything. For them we render exactly
   * what RLS returns (no client involvement filter) — RLS remains the security boundary.
   */
  externalDefectUser = false,
): Promise<DefectWithDetails[]> {
  if (!userId) return [];

  // ── Offline path ──
  if (!isOnline && offlineEnabled) {
    const cached = await getCachedData<DefectWithDetails[]>(DEFECTS_CACHE_KEY);
    const defects = cached?.data ?? [];

    // Merge offline-created defects from sync queue
    try {
      const queue = await dequeueAll();
      const offlineDefects = queue
        .filter((e) => e.entityType === "defect" && e.operation === "INSERT")
        .map((e) => offlineEntryToDefect(e));
      return [...offlineDefects, ...defects];
    } catch {
      return defects;
    }
  }

  let query = supabase
    .from("v_defects_with_details")
    .select(DEFECTS_VIEW_COLUMNS)
    .is("deleted_at", null)
    // MGM-18 (TES-481): nur Top-Level-Maengel in der Hauptliste — Unteraufgaben
    // (parent_defect_id gesetzt) erscheinen ausschliesslich im Subtasks-Panel des Parents,
    // nicht als eigenstaendige Eintraege (sonst Doppelzaehlung in Counts/Liste).
    .is("parent_defect_id", null)
    .order("created_at", { ascending: false });

  // Filter by user involvement if not admin/QM. Skipped for external defect users
  // (TES-626): their visibility is anchor-bound by RLS, not creator/assignee, so the
  // involvement filter would hide their anchored defects. RLS still gates every row.
  if (!canSeeAll && !externalDefectUser) {
    // TES-1989: Gruppen-Queue gehört zur Involvement-Menge — ein Fachbereichs-
    // Mitglied MUSS bearbeiterlose Mängel seiner Gruppen sehen, um sie per
    // "Ich übernehme" claimen zu können (v2.3 F-RBAC-OFFB-05). Ohne diesen
    // Zweig zeigte die Liste "Gesamt (0)", während das Nav-Badge zählte.
    // group_effective_members (View, mig 330/334) vereint statische + dynamische
    // Mitgliedschaft; ein Lookup-Fehler degradiert bewusst auf den alten Filter,
    // damit eigene Mängel nie verschwinden. RLS bleibt die Sicherheitsgrenze.
    // Die Queue-IDs werden über die defects-TABELLE aufgelöst (Copilot #757):
    // v_defects_with_details exponiert assigned_group_id nicht (Mig 306) — ein
    // direkter View-Filter würde mit 42703 die ganze Liste brechen.
    let groupIds: string[] = [];
    const { data: groupRows, error: groupError } = await supabase
      .from("group_effective_members")
      .select("group_id")
      .eq("user_id", userId);
    if (!groupError && groupRows) {
      groupIds = groupRows.map((row) => row.group_id).filter(Boolean) as string[];
    }
    let queueIds: string[] = [];
    if (groupIds.length > 0) {
      let queueQuery = supabase
        .from("defects")
        .select("id")
        .in("assigned_group_id", groupIds)
        .is("assigned_to", null)
        .is("deleted_at", null)
        .is("parent_defect_id", null);
      // Team-Scope schon im Queue-Lookup (Copilot #757): sonst sammelt die
      // ID-Liste team-fremde Treffer, die die View später eh wegfiltert.
      if (teamId) {
        queueQuery = queueQuery.eq("team_id", teamId);
      }
      const { data: queueRows, error: queueError } = await queueQuery;
      if (!queueError && queueRows) {
        queueIds = queueRows.map((row) => row.id).filter(Boolean) as string[];
      }
    }
    const involvement = [`assigned_to.eq.${userId}`, `created_by.eq.${userId}`];
    if (queueIds.length > 0) {
      // assigned_to.is.null auch im View-Filter (Copilot #757): wird ein Queue-
      // Mangel zwischen Lookup und Fetch geclaimt, fällt er hier wieder raus
      // (stabile Semantik statt Race-Flicker).
      involvement.push(`and(id.in.(${queueIds.join(",")}),assigned_to.is.null)`);
    }
    query = query.or(involvement.join(","));
  }

  // Server-side team filter
  if (teamId) {
    query = query.eq("team_id", teamId);
  }

  const { data: defectsData, error: defectsError } = await query;

  if (defectsError) throw defectsError;

  // The explicit projection includes the SAP columns (Mig. 306, TES-545) that are not yet
  // reflected in the generated view Row type, so PostgREST's static select-string inference
  // widens `defectsData` to its error type. Re-anchor to the known view Row shape before mapping.
  const rows = (defectsData ?? []) as unknown as DefectDetailsRow[];

  const enrichedDefects: DefectWithDetails[] = rows.map((defect) => {
    return {
      ...defect,
      images: normalizeDefectImagePaths(defect.images),
      isAssigneeUnavailable: isAssigneeUnavailable(defect.assigned_to_profile),
    } as unknown as DefectWithDetails;
  });

  // Cache for offline access
  if (offlineEnabled) {
    cacheData(DEFECTS_CACHE_KEY, enrichedDefects).catch(() => {});
  }

  return enrichedDefects;
}

export function useDefectsQuery(options?: TeamScopedQueryOptions) {
  const { user, isAdmin, isQualitaetsmanager, isExternalDefectUser } = useAuth();
  const canSeeAll = isAdmin || isQualitaetsmanager;
  const queryClient = useQueryClient();
  const { isOnline } = useOnlineStatus();
  const { enabled: offlineEnabled } = useFeatureFlag(
    "offline-mode",
    FEATURE_FLAGS["offline-mode"] ?? false,
  );
  const { teamId, enabled } = normalizeTeamScopedQueryOptions(options);

  const query = useQuery({
    queryKey: defectsKeys.list({
      userId: user?.id,
      canSeeAll,
      teamId,
      externalDefectUser: isExternalDefectUser,
    }),
    queryFn: () =>
      fetchDefects(user?.id, canSeeAll, isOnline, offlineEnabled, teamId, isExternalDefectUser),
    enabled: isUserQueryEnabled(user?.id, enabled),
    placeholderData: (previousData) => previousData,
    staleTime: 30_000, // 30s — prevents heavy view query on tab refocus
    ...(isOnline ? {} : { staleTime: Infinity, gcTime: Infinity, retry: false }),
  });

  // Use centralized realtime subscription with debouncing.
  // TES-2027: group_members(_effective) mit aufnehmen — die Gruppen-Queue (mangelbearbeiter)
  // wird über group_effective_members gescoped; ändert sich die eigene Gruppen-Mitgliedschaft,
  // muss die Mängel-Liste invalidieren (bislang nur in useTasksQuery nachgezogen, TES-1994).
  // Beide Tabellen sind bereits publiziert (RealtimeContext / Mig 370).
  useRealtimeSubscription(
    ["defects", "defect_comments", "group_members", "group_members_effective"],
    () => queryClient.invalidateQueries({ queryKey: defectsKeys.all }),
    { enabled: !!user },
  );

  return {
    defects: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * TES-1962 — Edit-Target für den konsolidierten DefectBaseDialog.
 *
 * Liefert das VOLLE `DefectWithDetails` (frischer Einzel-Fetch aus
 * `v_defects_with_details`) plus die Fachabteilung-Gruppe als `AssignmentValue`.
 * Ein Hook für alle drei Caller (Defects.tsx, Tasks.tsx, ItemDefectsList.tsx):
 * - die View exponiert `assigned_group_id` NICHT (Mig 306) → Gruppe kommt über
 *   die defects-Tabelle mit FK-gehintetem `team_group!defects_assigned_group_id_fkey`-Embed,
 * - Tasks/ItemDefectsList halten nur schlanke Typen (DefectCardData/ItemDefect)
 *   → der Einzel-Fetch verhindert Typ-Drift statt fragilem Feld-Mapping,
 * - DefectBaseDialog übernimmt `assignedGroup` nur beim Mount → Caller rendern
 *   den Dialog erst, wenn dieses Target geladen ist.
 * ──────────────────────────────────────────────────────────────────────────── */

// Bewusst NICHT exportiert: die Caller konsumieren nur den Hook (Rückgabetyp wird inferiert).
interface DefectEditTarget {
  defect: DefectWithDetails;
  /** Fachabteilung-Gruppe (id+name) — null, wenn keine zugewiesen ist. */
  assignedGroup: { id: string; name: string } | null;
}

async function fetchDefectEditTarget(defectId: string): Promise<DefectEditTarget> {
  const [viewResult, groupResult] = await Promise.all([
    supabase
      .from("v_defects_with_details")
      .select(DEFECTS_VIEW_COLUMNS)
      .eq("id", defectId)
      .single(),
    supabase
      .from("defects")
      // FK-Hint PFLICHT: defects hat ZWEI FKs auf team_group (assigned_group_id +
      // created_by_group_id) — ohne Hint lehnt PostgREST den Embed mit PGRST201 ab.
      .select("assigned_group_id, team_group!defects_assigned_group_id_fkey ( id, name )")
      .eq("id", defectId)
      .single(),
  ]);
  if (viewResult.error) throw viewResult.error;
  if (groupResult.error) throw groupResult.error;

  const row = viewResult.data as unknown as DefectDetailsRow;
  const defect = {
    ...row,
    images: normalizeDefectImagePaths(row.images),
    isAssigneeUnavailable: isAssigneeUnavailable(row.assigned_to_profile),
  } as unknown as DefectWithDetails;

  const groupRow = groupResult.data as unknown as {
    assigned_group_id: string | null;
    team_group: { id: string; name: string } | null;
  };
  const assignedGroup = groupRow.team_group
    ? { id: groupRow.team_group.id, name: groupRow.team_group.name }
    : null;

  return { defect, assignedGroup };
}

/** Frisches Defect + Gruppe für den Edit-Dialog; `null` deaktiviert den Fetch. */
export function useDefectEditTarget(defectId: string | null) {
  return useQuery({
    queryKey: defectsKeys.editTarget(defectId),
    queryFn: () => fetchDefectEditTarget(defectId as string),
    enabled: !!defectId,
    staleTime: 0, // Edit will immer Server-Wahrheit sehen
  });
}
